import React from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, Volume2, CheckCircle, Clock } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';

const Sidebar = ({
  tourMode,
  setTourMode,
  currentExhibit,
  currentTrack,
  exhibits,
  visitedExhibits,
  selectExhibit,
  isPlaying,
  togglePlayPause,
  toggleVisited,
  prevTrack,
  nextTrack,
  audioRef,
  formatTime,
  currentTime,
  containerRef
}: any) => {
  const { t } = useLocale();

  return (
    <div ref={containerRef} className="w-full backdrop-blur-sm border-t lg:border-t lg:border-l-0 flex flex-col lg:max-h-auto lg:h-auto lg:fixed lg:bottom-0 lg:left-0 lg:right-0 lg:z-50 pb-safe overscroll-none" style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--border-primary)', touchAction: 'none' }}>
      {/* Mobile Layout - Current Style */}
      <div className="lg:hidden">
        {/* Current Exhibit Info Counter & Title */}
        <div className="p-4 h-32 flex flex-col justify-start" style={{ borderColor: 'var(--border-primary)' }}>
          {/* Zone and Counter Row */}
          <div className="flex items-center justify-center gap-2 mb-2 min-h-8">
            <div className="rounded-lg flex items-center justify-center w-20 h-8 flex-shrink-0" style={{ backgroundColor: 'var(--color-capsule)' }}>
              <span className="text-base font-medium" style={{ color: 'var(--color-capsule-text)' }}>{currentExhibit.order} / {exhibits.length}</span>
            </div>
            <div className="text-center">
              <div className="text-xl font-medium" style={{ color: 'var(--text-accent)' }}>
                {currentExhibit.zone}
              </div>
            </div>
          </div>
          {/* Title and Description Row */}
          <div className="min-w-0 text-center min-h-8">
            <div className="text-xl leading-tight" style={{ color: 'var(--text-accent)' }}>
              <span className="font-medium">{currentExhibit.title}</span>
              <span className="ml-2">{currentExhibit.description}</span>
            </div>
          </div>
        </div>
        {/* Audio Player */}
        <div className="p-4 h-32 flex items-center">
          {/* Audio Controls */}
          <div className="flex items-center justify-center gap-3 w-full">
            <button
              onClick={prevTrack}
              disabled={currentTrack === 0}
              className="p-3 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
              style={{ backgroundColor: 'transparent' }}
            >
              <ChevronLeft className="w-20 h-32" style={{ color: 'var(--color-accent)' }} />
            </button>
            <button
              onClick={togglePlayPause}
              disabled={!currentExhibit.audio}
              className="p-3 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
              style={{ 
                backgroundColor: 'transparent',
                cursor: currentExhibit.audio ? 'pointer' : 'not-allowed'
              }}
            >
              {isPlaying && currentExhibit.audio ? (
                <Pause className="w-16 h-24" style={{ color: 'var(--color-accent)' }} />
              ) : (
                <Play className="w-16 h-24" style={{ color: 'var(--color-accent)' }} />
              )}
            </button>
            <button
              onClick={nextTrack}
              disabled={currentTrack === exhibits.length - 1}
              className="p-3 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
              style={{ backgroundColor: 'transparent' }}
            >
              <ChevronRight className="w-20 h-32" style={{ color: 'var(--color-accent)' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Left info + Center player + Right space */}
      <div className="hidden lg:flex items-center px-6 py-4">
        
        {/* Left Container: Progress Counter + Text Content */}
        <div className="flex items-center gap-6 flex-1">
          {/* Progress Counter */}
          <div className="flex items-center flex-shrink-0">
            <div className="rounded-lg flex items-center justify-center w-20 h-20" style={{ backgroundColor: 'var(--color-capsule)' }}>
              <span className="text-base font-medium" style={{ color: 'var(--color-capsule-text)' }}>{currentExhibit.order} / {exhibits.length}</span>
            </div>
          </div>

          {/* Text Content */}
          <div className="flex flex-col gap-0 flex-1 min-w-0">
            {/* Zone */}
            <div className="text-lg font-medium text-left" style={{ color: 'var(--text-accent)' }}>
              {currentExhibit.zone}
            </div>
            
            {/* Title */}
            <div className="text-lg font-semibold text-left" style={{ color: 'var(--text-accent)' }}>
              {currentExhibit.title}
            </div>
            
            {/* Description */}
            <div className="text-sm text-left" style={{ color: 'var(--text-accent)' }}>
              {currentExhibit.description}
            </div>
          </div>
        </div>

        {/* Center Container: Audio Controls */}
        <div className="flex items-center justify-center gap-2 flex-shrink-0">
          <button
            onClick={prevTrack}
            disabled={currentTrack === 0}
            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
            style={{ backgroundColor: 'transparent' }}
          >
            <ChevronLeft className="w-16 h-24" style={{ color: 'var(--color-accent)' }} />
          </button>
          <button
            onClick={togglePlayPause}
            disabled={!currentExhibit.audio}
            className="p-2 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
            style={{ 
              backgroundColor: 'transparent',
              cursor: currentExhibit.audio ? 'pointer' : 'not-allowed'
            }}
          >
            {isPlaying && currentExhibit.audio ? (
              <Pause className="w-12 h-16" style={{ color: 'var(--color-accent)' }} />
            ) : (
              <Play className="w-12 h-16" style={{ color: 'var(--color-accent)' }} />
            )}
          </button>
          <button
            onClick={nextTrack}
            disabled={currentTrack === exhibits.length - 1}
            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-opacity uppercase"
            style={{ backgroundColor: 'transparent' }}
          >
            <ChevronRight className="w-16 h-24" style={{ color: 'var(--color-accent)' }} />
          </button>
        </div>

        {/* Right Container: Empty space for balance */}
        <div className="flex-1"></div>
      </div>
    </div>
  );
};

export default Sidebar; 